import { cva } from "class-variance-authority";

/**
 * A button's look, for a control that must be something else — a link that
 * reads as a button.
 */
export const buttonVariants = cva(
  // Focus is visible by default rather than opt-in: a control nobody can reach
  // by keyboard is a control support staff cannot use during an incident.
  //
  // An array, and it has to be. This was four strings joined with `+`, and two
  // of the joins had no space at the boundary — so they concatenated into
  // `select-nonefocus-visible:outline-2` and
  // `active:scale-[0.98]disabled:pointer-events-none`, Tailwind emitted
  // neither, and **no button in the console had a focus ring** while a
  // disabled one still took pointer events. The sentence directly above was
  // false for as long as it had been written.
  //
  // Adding the spaces back does not fix it: `prettier-plugin-tailwindcss` is
  // configured over `cva` (see `.prettierrc`'s `tailwindFunctions`), it reads
  // each string in here as a class list, and it trims them — which is where
  // the spaces went in the first place and where they would go again on the
  // next format. An array has no boundaries to lose: clsx joins the elements,
  // and the plugin is free to sort inside each one.
  [
    "inline-flex items-center justify-center gap-2 font-sans font-medium tracking-[-0.01em]",
    "mw-press rounded-[var(--radius-control)] select-none",
    "disabled:pointer-events-none disabled:opacity-50",
  ],
  {
    variants: {
      variant: {
        primary: "bg-accent text-accent-ink hover:brightness-[1.06]",
        secondary:
          "border border-rule bg-surface text-ink hover:border-rule-strong hover:bg-surface-2",
        ghost: "text-ink-2 [--mw-lift:0px] hover:bg-surface-2 hover:text-ink",
        // Destructive actions look destructive. Relying on the label alone is
        // how someone deletes a production release meaning to archive it.
        danger: "bg-critical text-critical-ink hover:brightness-[1.06]",
        // The same argument one step down the severity ramp, for a button that
        // acts on the state its neighbouring badge is warning about — "Declare
        // it", beside "no rule can use this". A `secondary` button there reads
        // as one of several equal options on the row when it is the only thing
        // on the row that fixes anything.
        warning: "bg-warning text-warning-ink hover:brightness-[1.06]",
        // One pass of accent light across the button on hover. Never loops.
        djinn: "mw-lightpass bg-accent text-accent-ink hover:brightness-[1.06]",
      },
      size: {
        sm: "h-9 px-3.5 text-ui",
        md: "h-11 px-5 text-ui",
        lg: "h-12 px-6 text-prose",
      },
      // Opt-in: a button set at the page's weight rather than 500, for a list
      // of choices that are not actions (suggestion rows, did-you-mean rows).
      weight: {
        regular: "font-normal",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  },
);
